import type { LitSquadInventoryEntry, LitSquadInventoryItemDetail } from '@/lib/api';

/**
 * Case-insensitive presence check for a key on an item-metadata object.
 * Legacy LitSquad imports use PascalCase keys (`Eyes`, `Group`) while
 * newer crafted items use lowerCamelCase (`eyes`, `group`), so name
 * matches must be case-insensitive.
 */
function hasMetadataField(meta: Record<string, unknown> | null | undefined, name: string): boolean {
  if (!meta) return false;
  const lower = name.toLowerCase();
  for (const key of Object.keys(meta)) {
    if (key.toLowerCase() === lower) return true;
  }
  return false;
}

/**
 * Returns `true` if a LitSquad inventory entry represents a playable
 * character (an avatar base) and `false` if it represents a crafted or
 * legacy-imported accessory.
 *
 * Legacy import buckets BOTH characters and accessories under the same
 * 17 `litdef_species_*` definition ids, so the def-id alone can't
 * discriminate. The shape of the per-item `metadata` is the source of
 * truth:
 *
 *   - Characters carry the rendering attributes `Eyes`, `Ears`, `Face`,
 *     `Tail`, etc., and never have a `Group`.
 *   - Accessories carry a `Group` (e.g. `HeadItems`, `Shirts`,
 *     `HoldingLeft`) and never have body-part attributes.
 *
 * If metadata is missing or ambiguous (e.g. admin-minted items without a
 * legacy stats payload), the display name pattern is used as a fallback:
 *
 *   - `"LitSquad <Species> <number>"`   → character
 *   - `"LitSquad <Species> <words...>"` → accessory
 *
 * The default when no signal is present is "character" — this matches
 * how plain `litdef_species_*` rows are seeded by the migration.
 */
export function isLitSquadCharacter(
  entry: Pick<LitSquadInventoryEntry, 'itemMetadata' | 'displayName'>,
): boolean {
  const meta = entry.itemMetadata;
  if (hasMetadataField(meta, 'eyes')) return true;
  if (hasMetadataField(meta, 'group')) return false;

  const name = entry.displayName?.trim();
  if (name) {
    // Characters look like "LitSquad Dog 100018"; accessories look like
    // "LitSquad Dog Golf Club". After the species token, characters have
    // a numeric instance suffix; accessories have one or more words.
    const match = name.match(/^LitSquad\s+\S+\s+(.+)$/i);
    if (match) {
      return /^\d+$/.test(match[1].trim());
    }
    // "LitSquad Dog" with no suffix — admin-minted character template.
    if (/^LitSquad\s+\S+$/i.test(name)) return true;
  }

  return true;
}

/**
 * Validates that a URI is a safe HTTP(S) URL suitable for use as an image src.
 */
export function isSafeImageUri(uri: string | null | undefined): boolean {
  if (!uri) return false;
  try {
    const url = new URL(typeof uri === 'string' ? uri.trim() : uri);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

/**
 * Preferred display name for a LitSquad inventory row. Falls back to the
 * species-level definition name when the item has no per-item override
 * (e.g. admin-minted items without a legacy asset_name).
 */
export function resolveLitSquadDisplayName(
  entry: Pick<LitSquadInventoryEntry, 'displayName' | 'speciesName'>,
): string {
  return entry.displayName && entry.displayName.trim().length > 0
    ? entry.displayName
    : entry.speciesName;
}

/**
 * Preferred thumbnail for a LitSquad inventory row — the item-level override
 * (legacy icon rehosted under arche_dfe_icons/LitSquad/) takes precedence
 * over the species-level definition default.
 */
export function resolveLitSquadThumbnail(
  entry: Pick<LitSquadInventoryEntry, 'thumbnailUri' | 'definitionThumbnailUri'>,
): string | null {
  return entry.thumbnailUri ?? entry.definitionThumbnailUri ?? null;
}

export function resolveLitSquadItemDisplayName(
  item: Pick<LitSquadInventoryItemDetail, 'displayName'>,
  speciesName: string | null,
): string {
  if (item.displayName && item.displayName.trim().length > 0) return item.displayName;
  return speciesName ?? 'LitSquad NFT';
}

/**
 * Extracts the species ("Dog", "Bear", "Crab", …) of a LitSquad inventory
 * entry as a lowercase string, or `null` if no signal can be derived.
 *
 * Used to gate accessory layering in the avatar editor: only accessories
 * whose species matches the selected base character's species fit on
 * that character's silhouette (a Crab snowboard wouldn't render right on
 * a Dog).
 *
 * Tries signals in order of reliability:
 *   1. `itemMetadata.character` / `Character` — set by both legacy import
 *      and the LitSquad crafting service, so it survives admin-minted
 *      and crafted items as well as legacy imports.
 *   2. `itemDefId` parse — legacy-imported items live under the
 *      deterministic `litdef_species_<species>` ids.
 *   3. `displayName` parse — `"LitSquad <Species> ..."` is the legacy
 *      asset_name format.
 *   4. `speciesName` parse — definition-level fallback, same shape.
 *
 * Returns `null` when none of these resolve, in which case the caller
 * should treat the item as ineligible for species-gated UI rather than
 * defaulting to "matches everything".
 */
export function getLitSquadSpecies(
  entry: Pick<LitSquadInventoryEntry, 'itemDefId' | 'itemMetadata' | 'displayName' | 'speciesName'>,
): string | null {
  const meta = entry.itemMetadata;
  if (meta) {
    for (const key of Object.keys(meta)) {
      if (key.toLowerCase() === 'character') {
        const value = meta[key];
        if (typeof value === 'string' && value.trim().length > 0) {
          return value.trim().toLowerCase();
        }
      }
    }
  }

  const defMatch = entry.itemDefId.match(/^litdef_species_([a-z]+)$/i);
  if (defMatch) return defMatch[1].toLowerCase();

  const nameMatch = entry.displayName?.trim().match(/^LitSquad\s+(\S+)/i);
  if (nameMatch) return nameMatch[1].toLowerCase();

  const speciesMatch = entry.speciesName?.match(/^LitSquad\s+(\S+)/i);
  if (speciesMatch) return speciesMatch[1].toLowerCase();

  return null;
}

// =============================================================================
// White-background keying for legacy JPEG accessory thumbnails
// =============================================================================
//
// Legacy LitSquad accessory thumbnails (e.g. the bear dunce cap) are stored
// as JPEGs with a solid white background. When layered on a character for
// the avatar editor, that white square covers the character beneath. These
// helpers run an edge-flood chroma key: pixels reachable from the canvas
// border via 4-connectivity that are within `WHITE_THRESHOLD` of pure white
// are zeroed out. Internal white (e.g. the "D" letter inside the cap) is
// preserved because the flood-fill can't cross the cap's purple silhouette
// to reach it. PNGs that already have a transparent background are
// effectively no-ops because their border pixels have alpha 0 and aren't
// re-considered as solid backgrounds.
//
// Keep this client-side and pure: server-side prebake of alpha PNGs is the
// long-term plan but requires a batch job and a write-once migration.

/** Pixels at or above this brightness on every channel are treated as background. */
const WHITE_THRESHOLD = 240;

/**
 * Subset of the DOM `ImageData` interface we depend on. Pure JS code paths
 * (and tests) can pass any object with `data`, `width`, `height` — they
 * don't need a real DOM ImageData.
 */
export interface ImageDataLike {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

/**
 * Mutates `image.data` in place: any pixel within `WHITE_THRESHOLD` of pure
 * white that is reachable (via 4-connectivity) from the canvas border is
 * given alpha 0. Returns the same object for chainability.
 *
 * Pure pixel logic — no DOM dependencies. Wrapped by
 * {@link keyOutWhiteBackground} for the typical call site.
 */
export function keyOutWhiteBackgroundPixels<T extends ImageDataLike>(image: T): T {
  const { data, width, height } = image;
  const total = width * height;
  if (total === 0) return image;

  const isWhiteish = (idx: number): boolean => {
    const base = idx * 4;
    return (
      data[base] >= WHITE_THRESHOLD &&
      data[base + 1] >= WHITE_THRESHOLD &&
      data[base + 2] >= WHITE_THRESHOLD &&
      data[base + 3] !== 0 // already-transparent pixels don't need re-marking
    );
  };

  // BFS from every border pixel that is white-ish. Use a head-pointer queue
  // so dequeue is O(1) — Array.shift() on a 1024×1024 worst case would be
  // catastrophically slow.
  const isBackground = new Uint8Array(total);
  const queue: number[] = [];
  let head = 0;

  const seed = (idx: number): void => {
    if (!isBackground[idx] && isWhiteish(idx)) {
      isBackground[idx] = 1;
      queue.push(idx);
    }
  };

  for (let x = 0; x < width; x++) {
    seed(x); // top row
    seed((height - 1) * width + x); // bottom row
  }
  for (let y = 0; y < height; y++) {
    seed(y * width); // left column
    seed(y * width + (width - 1)); // right column
  }

  while (head < queue.length) {
    const idx = queue[head++];
    const x = idx % width;
    const y = (idx - x) / width;

    if (x > 0) seed(idx - 1);
    if (x < width - 1) seed(idx + 1);
    if (y > 0) seed(idx - width);
    if (y < height - 1) seed(idx + width);
  }

  // Zero alpha on every flagged background pixel.
  for (let i = 0; i < total; i++) {
    if (isBackground[i]) data[i * 4 + 3] = 0;
  }

  return image;
}

/**
 * DOM wrapper for {@link keyOutWhiteBackgroundPixels}. Draws `img` at its
 * natural size onto an offscreen canvas, runs the edge-flood chroma key,
 * and returns the canvas (which is itself a valid `CanvasImageSource` for
 * later `drawImage` calls).
 *
 * If the environment can't provide a 2D context or `getImageData` throws
 * (jsdom no-op contexts in tests, CORS-tainted canvases), falls back to
 * returning the original image untouched — the caller still gets a
 * usable `CanvasImageSource`.
 */
export function keyOutWhiteBackground(img: HTMLImageElement): CanvasImageSource {
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (!w || !h) return img;

  let canvas: HTMLCanvasElement;
  try {
    canvas = document.createElement('canvas');
  } catch {
    return img;
  }
  canvas.width = w;
  canvas.height = h;

  const ctx = canvas.getContext('2d');
  if (!ctx) return img;
  ctx.drawImage(img, 0, 0);

  let imageData: ImageData;
  try {
    imageData = ctx.getImageData(0, 0, w, h);
  } catch {
    // jsdom returns a non-functional context whose getImageData throws,
    // and a CORS-tainted canvas throws SecurityError. Either way we have
    // no pixel access — let the caller draw the raw image.
    return img;
  }

  keyOutWhiteBackgroundPixels(imageData);
  ctx.putImageData(imageData, 0, 0);
  return canvas;
}
